import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, Pencil, Shield, User } from "lucide-react";
import { FC } from "react";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import { locales } from "@recobra/i18n/runtime";
import { ProfileFormSchema } from "@recobra/schemas/user";
import { Avatar, AvatarBadge, AvatarFallback, AvatarImage } from "@recobra/ui/avatar";
import { Badge } from "@recobra/ui/badge";
import { Button } from "@recobra/ui/button";
import { Field, FieldContent, FieldError, FieldLabel, FieldSet } from "@recobra/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@recobra/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@recobra/ui/radio-group";
import { Separator } from "@recobra/ui/separator";

import { invalidateAuthSession } from "../lib";
import { isAppLocale } from "../lib";
import { useAuth } from "../lib";
import { useLocale } from "../lib";
import { profileApi } from "../lib/api";
import { profileUpdateInput } from "./ProfileFormValues";
import { ThemePreferenceField } from "./ThemePreferenceField";
import { useProfilePhotoUpload } from "./useProfilePhotoUpload";

export const ProfileForm: FC = () => {
  const queryClient = useQueryClient();
  const { locale, setLocale } = useLocale();
  const { photo, setPhoto, readingPhoto, photoInputRef, onPhotoInputChange } =
    useProfilePhotoUpload();
  const { displayName, picture, user, cognitoGroups, email } = useAuth();

  const fallbackLabel =
    email ?? user?.signInDetails?.loginId ?? user?.username ?? m.profile_account_fallback();
  const initialDisplayName = displayName ?? email ?? "";
  const initialPicture = picture ?? "";

  const mutation = useMutation({
    mutationFn: profileApi.updateProfile,
    onError: () => toast.error(m.profile_photo_save_failed()),
    onSuccess: async (data) => {
      if (data.picture !== undefined) {
        profileForm.setFieldValue("pictureUrl", data.picture ?? "");
      }
      setPhoto(undefined);
      toast.success(m.profile_updated_toast());
      await invalidateAuthSession(queryClient);
      await queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
    },
  });

  const profileForm = useForm({
    defaultValues: {
      profileName: initialDisplayName,
      pictureUrl: initialPicture,
      locale,
    },
    validators: {
      onSubmit: ProfileFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (value.locale !== locale) await setLocale(value.locale);

      mutation.mutate(profileUpdateInput(value.profileName, photo));
    },
  });

  const fieldsContent = (
    <>
      <profileForm.Field name="profileName">
        {(field) => (
          <Field className="min-w-0">
            <FieldLabel htmlFor="profile-name">{m.profile_display_name()}</FieldLabel>
            <InputGroup>
              <InputGroupAddon align="inline-start">
                <User className="size-4 text-muted-foreground/60" />
              </InputGroupAddon>
              <InputGroupInput
                id="profile-name"
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={fallbackLabel}
                autoComplete="name"
                aria-invalid={field.state.meta.errors.length > 0}
              />
            </InputGroup>
            {formatFieldErrors(field.state.meta.errors) ? (
              <FieldError>{formatFieldErrors(field.state.meta.errors)}</FieldError>
            ) : null}
          </Field>
        )}
      </profileForm.Field>

      <Separator />

      <FieldSet>
        <profileForm.Field name="locale">
          {(field) => (
            <RadioGroup
              value={field.state.value}
              onValueChange={(value) => {
                if (typeof value === "string" && isAppLocale(value)) {
                  field.handleChange(value);
                }
              }}
            >
              {locales.map((code) => (
                <FieldLabel key={code} htmlFor={`profile-locale-${code}`}>
                  <Field orientation="horizontal">
                    <FieldContent>{m.locale_label({ locale: code })}</FieldContent>
                    <RadioGroupItem value={code} id={`profile-locale-${code}`} />
                  </Field>
                </FieldLabel>
              ))}
            </RadioGroup>
          )}
        </profileForm.Field>
      </FieldSet>

      <Separator />

      <ThemePreferenceField />
    </>
  );

  const saveButton = (
    <Button
      type="submit"
      disabled={mutation.isPending || readingPhoto}
      size="lg"
      className="w-full"
    >
      {mutation.isPending ? m.profile_saving() : m.profile_save()}
    </Button>
  );

  const identityContent = (
    <>
      {/* cover background */}
      <div className="h-24 w-full bg-muted border-b border-border/10" />

      {/* profile details */}
      <div className="px-4 pb-4 pt-0 relative flex flex-col sm:flex-row sm:items-end gap-4">
        {/* avatar container with overlap */}
        <div className="relative -mt-8 shrink-0">
          <profileForm.Subscribe
            selector={(state) => ({
              profileName: state.values.profileName,
              pictureUrl: state.values.pictureUrl ?? "",
            })}
          >
            {({ profileName, pictureUrl }) => {
              const avatarUrl = photo ?? pictureUrl;

              return (
                <button
                  type="button"
                  className="relative rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
                  disabled={mutation.isPending || readingPhoto}
                  aria-label={avatarUrl ? m.profile_photo_change() : m.profile_photo_choose()}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Avatar size="xl">
                    {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
                    <AvatarFallback>
                      {userInitials(profileName || displayName || fallbackLabel)}
                    </AvatarFallback>
                    <AvatarBadge>
                      <Pencil />
                    </AvatarBadge>
                  </Avatar>
                </button>
              );
            }}
          </profileForm.Subscribe>
          <input
            ref={photoInputRef}
            hidden
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={mutation.isPending || readingPhoto}
            onChange={onPhotoInputChange}
          />
        </div>

        {/* user name, email, roles */}
        <div className="min-w-0 flex-1 pt-1">
          <profileForm.Subscribe selector={(state) => state.values.profileName}>
            {(profileName) => (
              <h3 className="text-lg font-bold tracking-tight text-foreground truncate">
                {profileName || displayName || fallbackLabel}
              </h3>
            )}
          </profileForm.Subscribe>

          {email && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 truncate">
              <Mail className="size-3.5 shrink-0 text-muted-foreground/60" />
              {email}
            </p>
          )}

          {cognitoGroups && cognitoGroups.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-xs uppercase font-bold tracking-wider text-muted-foreground/80 mr-1">
                {m.profile_role_badge()}:
              </span>
              {cognitoGroups.map((group) => (
                <Badge key={group} variant="secondary">
                  <Shield />
                  {m.cognito_group_label({ group })}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <form
      key={`${initialDisplayName}\0${initialPicture}`}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void profileForm.handleSubmit();
      }}
      className="flex-1 flex flex-col min-h-0 h-full bg-background"
    >
      {/* Sticky Header with Shrunk Colored Bar */}
      <div className="relative shrink-0 border-b border-border bg-card">{identityContent}</div>

      {/* Scrollable Content Area */}
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">{fieldsContent}</div>

      {/* Sticky Footer */}
      <div className="shrink-0 border-t border-border bg-card p-4">{saveButton}</div>
    </form>
  );
};

const userInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return m.common_unknown();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
};

const formatFieldErrors = (errors: ReadonlyArray<unknown>): string | null => {
  const messages = errors
    .map((err) => {
      if (typeof err === "string") return err;
      if (
        typeof err === "object" &&
        err !== null &&
        "message" in err &&
        typeof err.message === "string"
      )
        return err.message;

      return undefined;
    })
    .filter((msg): msg is string => Boolean(msg));

  return messages.length > 0 ? messages.join(", ") : null;
};
